// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what a link line looks like
//  ---------------------------------------------------------------------------
//  md-embed.ts decides WHAT a line is; this draws it. Four things can come back
//  and each gets the treatment it deserves rather than a common denominator:
//
//    video     the host's own player, framed by us, with our sandbox
//    image     the picture, contained, on no background of its own
//    file      the browser's own PDF viewer, or an honest line about where it is
//    link      a card naming where it goes — the only kind we cannot show
//
//  ONE COMPONENT, TWO SURFACES. The board draws these inside an element's box
//  and the record sheet draws them between paragraphs. Both call EmbedView, so a
//  video cannot come to look like two different things depending on which door
//  you opened it from. EmbedCard adds what only the sheet has: chrome to act on
//  it, a ring when the caret is standing on it, and a corner to drag.
//
//  ── ON NOT FETCHING ANYTHING TO DECORATE A BOOKMARK ───────────────────────
//  Notion's bookmark cards carry a title, a description and a favicon, and it
//  gets them by fetching the page on a server of its own. We do not, and the
//  reason is the app rather than the effort: this is somebody's journal, and
//  quietly asking a third party — or our own worker — about every address in it
//  would make a private record into a request log. So a bookmark card says what
//  the address itself says, which is the host and the name of the thing. Videos
//  are the exception already argued in md-media.ts: their titles come from an
//  oEmbed endpoint the writer's own paste already pointed at.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStateE, useEffect: useEffectE } = React;
const { ink: inkE, mdRgba: rgbaE } = window;

/* ── a document, and whether the bytes have got here yet ─────────────────────
   The bytes travel now — md-file.ts hands them to the blob door and any device
   can ask for them by name — so the honest states are three rather than two:
   looking, here, and not here yet. What must never happen is the fourth thing,
   a broken frame with no explanation, which is what you get if you assume the
   bytes are wherever the record is.

   AND THE LINE HAS TO BE THE RIGHT ONE OF TWO. A document dropped before 1.9
   is named by an id no store ever heard of; its bytes really are only on the
   device it was added to, and they always will be. A document dropped since is
   named by its hash, so "not here" means not here YET. mdFileTravels is what
   tells the two apart, and saying the wrong one is either promising a file that
   is never coming or writing off one that is on its way.

   The viewer is the BROWSER'S OWN, handed a blob URL. Vendoring pdf.js is the
   alternative and it is about a megabyte plus its own lazy-load, to reproduce
   something already installed. Stated plainly: the chrome around the page
   differs between browsers, and iOS Safari renders PDFs in a frame poorly. That
   is the trade, and it is reversible — this component is the only thing that
   would have to change. */
function FileFrame({ id, name, light }) {
  const [url, setUrl] = useStateE(undefined);   // undefined = still looking

  useEffectE(() => {
    let alive = true;
    let made = '';
    if (!window.mdFileGet) { setUrl(null); return undefined; }
    window.mdFileGet(id).then((blob) => {
      if (!alive) { return; }
      if (!blob) { setUrl(null); return; }
      /* THE FRAME BELOW HAS NO SANDBOX, and a blob: URL is same-origin with this
         document — so whatever is in it runs beside the journal, the session and
         the store. That is safe for a PDF, which the browser renders and never
         executes, and it is the worst thing in this codebase for anything else.

         Nothing the app writes can get another type here: a manifestation is not
         an embed and never becomes an `mdfile:` line. But a person can type one
         by hand into their own record, and "no code path reaches this" is a
         weaker promise than "this refuses". So the bytes are asked what they are
         before they are framed. */
      if (window.mdFileIsCraft && window.mdFileIsCraft(blob.type)) { setUrl(null); return; }
      made = URL.createObjectURL(blob);
      setUrl(made);
    });
    /* Revoked on the way out, every time. An object URL is a reference the
       document holds until it is dropped, so a board opened and closed all
       afternoon would otherwise pin every PDF it ever showed in memory. */
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [id]);

  const box = {
    width: '100%', height: '100%', boxSizing: 'border-box', display: 'block',
    borderRadius: 20, overflow: 'hidden',
    border: `0.75px solid ${inkE(light ? 0.1 : 0.16)}`,
  };

  /* `travels` is asked at render rather than remembered, for the same reason
     ink() is: a build with md-file.ts absent should read as the older, truer
     sentence rather than promise an arrival nothing is arranging. */
  const travels = !!(window.mdFileTravels && window.mdFileTravels(id));

  /* WHERE THE BYTES ARE (5.2), in the one place somebody is looking at them.

     A file has no document of its own — it is named by the hash of its own
     bytes — so `doc` is handed in rather than read from mdSyncStates: a name a
     store could be asked for is the only document-shaped fact there is, and a
     name minted before 1.9 is one no store has ever heard of. That is the same
     distinction the sentence below already draws, said again in a dot, and the
     two must never disagree.

     `here` is what the frame already learned. `undefined` is still looking,
     and the mark says nothing rather than guessing — for the few frames it
     takes, an absent dot is honest and a wrong one is not. */
  const Mark = window.MdWhereMark;
  const where = window.mdWhereRead ? window.mdWhereRead({
    doc: travels ? 'synced' : 'local',
    hashes: travels ? [id] : [],
    here: url === undefined ? null : !!url,
  }) : null;
  const mark = Mark && where ? <Mark where={where} /> : null;

  if (url) {
    return (
      <div style={{ ...box, position: 'relative', background: light ? '#ffffff' : '#1a1c24' }}>
        <iframe src={url} title={name}
          style={{ width: '100%', height: '100%', display: 'block', border: 'none' }} />
        {mark && <span style={{ position: 'absolute', top: 11, insetInlineEnd: 11,
          display: 'flex' }}>{mark}</span>}
      </div>);
  }

  /* Every one of "still looking", "on its way" and "not here" uses the same
     quiet slot a missing record gets, so nothing on this board ever shouts. */
  return (
    <div style={{ ...box, border: `1px dashed ${inkE(light ? 0.14 : 0.2)}`,
      display: 'grid', placeItems: 'center', padding: 16, gap: 6,
      textAlign: 'center', fontFamily: 'var(--md-body)' }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        minWidth: 0, color: inkE(0.7), fontSize: 13.5, fontWeight: 600 }}>{mark}<span
          style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span></span>
      <span style={{ color: inkE(0.32), fontSize: 12, fontWeight: 550, lineHeight: 1.5 }}>
        {url === undefined ? 'Looking for this file…'
          : travels ? 'Not on this device yet — it arrives once it has uploaded'
            : 'This file is on the device it was added to'}
      </span>
    </div>);
}

/* ── a video, or a picture ───────────────────────────────────────────────────
   THE FRAME IS OURS, AND THE ENGINE'S EMBED ALLOWLIST IS NEVER CONSULTED.
   Excalidraw can frame third-party hosts by itself, and none of that machinery
   is reachable from here: a board element's `link` is a ManDana ref and nothing
   else, and the media URL lives inside the record's text where the engine never
   looks. So every attribute below — what the frame may do, where it may
   navigate, what referrer it leaks — is decided in this file rather than by a
   library's defaults.

   `sandbox` is the one that earns its keep. Without it a framed page can
   navigate the WHOLE TAB away from the app; the default sandbox forbids that,
   and the four capabilities added back are the ones a video player genuinely
   needs. `allow-same-origin` sounds alarming and is not: it is same-origin with
   respect to youtube-nocookie.com, not to us, and dropping it stops the player
   loading at all. */
function MediaFrame({ media, title, light, onNatural }) {
  /* Keyed on the src by every caller, so swapping the link clears a failure
     that belonged to the previous picture rather than condemning the new one. */
  const [broken, setBroken] = useStateE(false);
  const box = {
    width: '100%', height: '100%', boxSizing: 'border-box', display: 'block',
    borderRadius: 20, overflow: 'hidden',
    border: `0.75px solid ${inkE(light ? 0.1 : 0.16)}`,
    boxShadow: light ? '0 6px 18px rgba(40,60,110,0.10)' : '0 8px 22px rgba(0,0,0,0.42)',
  };

  if (media.kind === 'image') {
    /* A picture on somebody else's server is one they can take away, and plenty
       of hosts simply refuse to be embedded. Left alone, a broken <img> renders
       its alt text — so the URL sprawls across the board in the app's own font,
       which reads as a bug rather than as a fact about the internet. Say it
       quietly instead, in the same dashed slot a missing record gets. */
    if (broken) {
      return (
        <div style={{ ...box, border: `1px dashed ${inkE(light ? 0.14 : 0.2)}`, boxShadow: 'none',
          display: 'grid', placeItems: 'center', padding: 12,
          color: inkE(0.32), fontSize: 12.5, fontWeight: 550, textAlign: 'center',
          fontFamily: 'var(--md-body)' }}>
          This picture could not be loaded
        </div>);
    }
    /* Contained rather than cropped, and on no background of its own — a
       picture laid on a whiteboard shows the board around it, and cropping
       would silently hide part of what somebody put there. */
    return (
      <img src={media.src} alt={title} draggable={false} onError={() => setBroken(true)}
        /* Its true shape, told to whoever is drawing it. Nothing is written down
           and nothing moves on its own — but the moment somebody drags the
           corner, the picture keeps its own proportions instead of the 16:9 the
           slot had to guess before it loaded. */
        onLoad={(e) => { if (onNatural && e.target.naturalWidth) onNatural(e.target.naturalWidth / e.target.naturalHeight); }}
        style={{ ...box, objectFit: 'contain', background: 'transparent' }} />
    );
  }

  return (
    <div style={{ ...box, background: '#000' }}>
      <iframe
        src={media.src}
        title={title}
        style={{ width: '100%', height: '100%', display: 'block', border: 'none' }}
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" />
    </div>
  );
}

/* ── an address we can name but not show ─────────────────────────────────────
   Most of the web refuses to be framed, and our own Content-Security-Policy
   names the two hosts that may be (see public/_headers). Rather than a broken
   rectangle, a card: what the writer called it, or the last thing the address
   itself says, over the host it belongs to.

   It is a real link and it opens in a new tab, re-checked through mdLinkHref on
   the way out even though md-embed.ts already did — the check is a regex and a
   parse, and what it prevents is arbitrary script running in the page that
   holds someone's journal. Same discipline as md-markdown's mdOpenLink. */
function mdEmbedName(embed) {
  if (embed.label) return embed.label;
  let u = null;
  try { u = new URL(embed.href); } catch (e) { return embed.target; }
  const last = u.pathname.split('/').filter(Boolean).pop() || '';
  let name = last;
  try { name = decodeURIComponent(last); } catch (e) { /* keep it raw */ }
  name = name.replace(/\.[a-z0-9]{1,8}$/i, '').replace(/[_-]+/g, ' ').trim();
  return name || u.hostname.replace(/^www\./, '');
}

function BookmarkCard({ embed, light }) {
  let host = '';
  try { host = new URL(embed.href).hostname.replace(/^www\./, ''); } catch (e) { host = ''; }
  const open = () => {
    const href = window.mdLinkHref && window.mdLinkHref(embed.target);
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };
  return (
    <button onClick={open} title={embed.href}
      style={{ width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex',
        alignItems: 'center', gap: 13, textAlign: 'start', cursor: 'pointer',
        padding: '0 16px', borderRadius: 18, fontFamily: 'var(--md-body)',
        background: light ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.045)',
        border: `0.75px solid ${inkE(light ? 0.1 : 0.16)}`, overflow: 'hidden' }}>
      <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 11, flexShrink: 0,
        display: 'grid', placeItems: 'center', background: inkE(light ? 0.05 : 0.09) }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="8.6" stroke={inkE(0.5)} strokeWidth="1.6" />
          <path d="M3.6 12h16.8M12 3.4c2.2 2.4 3.3 5.3 3.3 8.6s-1.1 6.2-3.3 8.6c-2.2-2.4-3.3-5.3-3.3-8.6S9.8 5.8 12 3.4z"
            stroke={inkE(0.5)} strokeWidth="1.4" />
        </svg>
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'block' }}>
        <span style={{ display: 'block', color: inkE(0.9), fontSize: 14.5, fontWeight: 620,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {mdEmbedName(embed)}
        </span>
        <span style={{ display: 'block', color: inkE(0.45), fontSize: 12, marginTop: 3,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{host}</span>
      </span>
      <span aria-hidden="true" style={{ flexShrink: 0, color: inkE(0.3) }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M8 5l7 7-7 7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>);
}

/* ── the formats, named once ────────────────────────────────────────────────
   An address does not always admit what it is: a CDN serves a photograph from
   a path with no extension on it, and a video is sometimes a reference rather
   than something to watch here. So the writer gets the say — and gets it in
   TWO places, which is why this list is one list.

   The paste menu offers it at the moment the link lands, because that is when
   the writer is thinking about it. The card's own ⋯ offers it forever after,
   because that is when they notice the guess was wrong. Two surfaces, one set
   of words: a menu that says "Picture" here and "Show picture" there is two
   features to the person reading them. */
const MD_EMBED_FORMATS = {
  video: { label: 'Video', hint: 'Played here' },
  image: { label: 'Picture', hint: 'Shown here' },
  link: { label: 'Card', hint: 'Named here, opened there' },
  plain: { label: 'Plain link', hint: 'Just the address' },
};

/** What this line could be drawn as, which one it is now, and the words for
    both. md-embed.ts decides what is honestly on offer; this only dresses it. */
function mdEmbedFormatsOf(line) {
  const e = window.mdEmbedLine ? window.mdEmbedLine(line) : null;
  const kinds = e && window.mdEmbedKinds ? window.mdEmbedKinds(e) : [];
  return kinds.map((id) => ({ id, ...MD_EMBED_FORMATS[id], on: id === e.kind }));
}

/** Whatever this line turned out to be, drawn to fill the box it is given. */
function EmbedView({ embed, light, title, onNatural }) {
  if (embed.kind === 'file') {
    return <FileFrame id={embed.file} name={embed.label || 'Document'} light={light} />;
  }
  if (embed.media) {
    return <MediaFrame media={embed.media} title={embed.label || title || ''}
      light={light} onNatural={onNatural} />;
  }
  return <BookmarkCard embed={embed} light={light} />;
}

/* ── the geometry ────────────────────────────────────────────────────────────
   What box a kind wants before anything has loaded, so the writing does not
   jump when it does. A ratio rather than a height wherever the shape is known:
   a video is 16:9 at whatever measure the column turns out to be.

   The document is the one that gets a WIDTH cap, and it is the whole answer to
   "the PDF frame is square". A page is taller than it is wide; given the full
   measure of a record it would either be a thousand pixels tall or, capped by
   height, a squat letterbox with a page floating in the middle of it. Capping
   the WIDTH instead puts a page-shaped page in the column. */
const MD_EMBED_LINK_H = 84;
const MD_EMBED_FILE_W = 560;
const MD_EMBED_PAGE = 1 / 1.294;      // A4 in the browser's own viewer, chrome included

function mdEmbedBox(embed) {
  if (embed.w > 0 && embed.h > 0) return { key: embed.key, kind: embed.kind, width: embed.w, height: embed.h };
  if (embed.kind === 'file') return { key: embed.key, kind: embed.kind, ratio: MD_EMBED_PAGE, maxWidth: MD_EMBED_FILE_W };
  if (embed.kind === 'link') return { key: embed.key, kind: embed.kind, height: MD_EMBED_LINK_H };
  /* A picture's own shape is not knowable before it loads, and reserving the
     wrong box makes the writing jump when it does. 16:9 is the reserve for both
     kinds, and a picture is CONTAINED in it — so a tall photograph sits in the
     middle of its slot until somebody drags it to the shape they want. */
  return { key: embed.key, kind: embed.kind, ratio: window.MD_MEDIA_ASPECT || 16 / 9 };
}

/* ── the sheet's card ────────────────────────────────────────────────────────
   The board hands its cards to the engine and gets selection, resize and a
   context menu for free. In the writing there is no engine, so this is where
   an embed becomes something you can act on:

     • a ring when the caret is standing on its line, because a caret that has
       nowhere to blink still has to be somewhere you can see;
     • a ⋯ on hover, with the two or three things there are to do;
     • a corner to drag.

   The controls appear on hover and on caret — never permanently. A record full
   of buttons is a record you are editing rather than one you are reading. */
/* A video is 16:9 and a picture is whatever shape it is; a document and a card
   are sized freely, because how tall a PDF viewer should be is a decision about
   reading, not about the page. Returning 0 means "both axes are the writer's". */
function mdEmbedRatio(embed, natural, width, height) {
  if (embed.kind === 'video') return window.MD_MEDIA_ASPECT || 16 / 9;
  if (embed.kind === 'image') return natural || (width && height ? width / height : 0) || (window.MD_MEDIA_ASPECT || 16 / 9);
  return 0;
}

function EmbedCard({ embed, raw, light, accent, active, width, height, title, onEdit }) {
  const [hot, setHot] = useStateE(false);
  const [menu, setMenu] = useStateE(false);
  const [drag, setDrag] = useStateE(null);
  const [natural, setNatural] = useStateE(0);
  const shown = hot || active || menu || !!drag;
  const formats = mdEmbedFormatsOf(raw);

  /* The size is written into the line when the finger comes UP, not while it
     moves: a drag is one act and the record's history should say so — otherwise
     ⌘Z after resizing a video takes forty presses to get back.
     In between, the card simply draws itself at the size being dragged and
     overflows the box the editor reserved for it. The writing below settles
     once, on release, rather than jumping under the hand doing the dragging. */
  const onGrab = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY;
    const w0 = width, h0 = height;
    const ratio = mdEmbedRatio(embed, natural, width, height);
    const min = window.MD_EMBED_MIN || 80;
    const max = window.MD_EMBED_MAX || 4000;
    const at = (ev) => {
      const w = Math.round(Math.min(max, Math.max(min, w0 + (ev.clientX - startX))));
      const h = ratio > 0
        ? Math.round(Math.min(max, Math.max(min, w / ratio)))
        : Math.round(Math.min(max, Math.max(min, h0 + (ev.clientY - startY))));
      return { w, h };
    };
    const move = (ev) => setDrag(at(ev));
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const size = at(ev);
      setDrag(null);
      onEdit && onEdit(window.mdEmbedResize(raw, size.w, size.h));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const act = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); setMenu(false); fn(); };
  const chip = {
    width: 26, height: 26, display: 'grid', placeItems: 'center', padding: 0,
    borderRadius: 8, cursor: 'pointer', color: '#fff',
    border: `0.75px solid ${rgbaE('#ffffff', 0.22)}`,
    background: 'rgba(18,20,28,0.62)',
    backdropFilter: 'blur(18px) saturate(160%)', WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.34)',
  };

  /* ── why the controls live in two small corners ───────────────────────────
     A video and a PDF are IFRAMES, and an iframe swallows the pointer: the
     moment it crosses the frame, this document stops seeing it and any hover on
     the card as a whole ends. Measured, not assumed — the ⋯ was simply
     unreachable on a video, which is the kind of thing that only shows up when
     you point at the real thing.

     So the two controls sit in two small always-live corners instead. They cost
     a 44×36 patch of the frame's top-right and a 26px square at its foot, and
     they buy chrome that appears wherever you reach for it. Everything else —
     the whole of the player — belongs to the player. */
  const zone = (extra) => ({ position: 'absolute', zIndex: 2, ...extra });
  const hover = { onMouseEnter: () => setHot(true), onMouseLeave: () => setHot(false) };

  return (
    <div style={{ position: 'relative',
      width: drag ? drag.w : '100%', height: drag ? drag.h : '100%' }}>
      <EmbedView embed={embed} light={light} title={title} onNatural={setNatural} />

      {/* The caret's own line, said in the only way a box with no text can say
          it. Drawn OUTSIDE the card (a ring, not a border) so it never covers a
          pixel of the picture. */}
      {(active || drag) &&
      <span aria-hidden="true" style={{ position: 'absolute', inset: -3, borderRadius: 23,
        border: `2px solid ${rgbaE(accent, 0.55)}`, pointerEvents: 'none' }} />}

      <div {...hover} style={zone({ top: 0, insetInlineEnd: 0, width: 46, height: 38,
        display: 'grid', placeItems: 'center' })}>
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenu((v) => !v); }}
          aria-label="Embed options" aria-haspopup="menu" aria-expanded={menu}
          /* Tabbing to it shows it: a control that can be reached but not seen
             is worse than one that is always drawn. */
          onFocus={() => setHot(true)} onBlur={() => setHot(false)}
          style={{ ...chip, background: menu ? rgbaE(accent, 0.9) : chip.background,
            /* Present in the tree at all times so it can be tabbed to and so the
               corner is always a live target; only its paint waits for a reason
               to appear. */
            opacity: shown ? 1 : 0, pointerEvents: shown ? 'auto' : 'none',
            transition: 'opacity 140ms ease' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="6" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="18" cy="12" r="1.7" />
          </svg>
        </button>
      </div>

      {menu &&
      <div role="menu" aria-label="Embed options"
        style={{ position: 'absolute', top: 42, insetInlineEnd: 10, zIndex: 3,
          width: 196, padding: '5px 0', borderRadius: 14,
          border: `0.75px solid ${inkE(light ? 0.1 : 0.16)}`,
          background: light ? 'rgba(255,255,255,0.96)' : 'rgba(26,28,36,0.96)',
          backdropFilter: 'blur(26px)', WebkitBackdropFilter: 'blur(26px)',
          boxShadow: '0 18px 44px rgba(0,0,0,0.34)', fontFamily: 'var(--md-body)' }}>
        {/* ── what it should be ───────────────────────────────────────────────
            First, because it is the only thing in this menu that changes what
            you are looking at — and because the guess it corrects is one you
            can only notice once the card is in front of you. A document has no
            rows here: bytes behind an mdfile: line are a document and nothing
            else, wherever they came from. */}
        {formats.length > 1 &&
        <React.Fragment>
          <div style={{ padding: '3px 14px 5px', fontSize: 10.5, fontWeight: 700,
            letterSpacing: 0.8, textTransform: 'uppercase', color: inkE(0.42) }}>Show as</div>
          {formats.map((f) => (
            <EmbedItem key={f.id} label={f.label} light={light} on={f.on} accent={accent}
              onClick={act(() => { if (!f.on) onEdit && onEdit(window.mdEmbedFormat(raw, f.id)); })} />))}
          <div aria-hidden="true" style={{ height: '0.75px', margin: '5px 10px',
            background: inkE(light ? 0.08 : 0.14) }} />
        </React.Fragment>}
        {embed.href &&
        <EmbedItem label="Open original" light={light} onClick={act(() => {
          const href = window.mdLinkHref && window.mdLinkHref(embed.target);
          if (href) window.open(href, '_blank', 'noopener,noreferrer');
        })} />}
        {embed.href &&
        <EmbedItem label="Copy link" light={light} onClick={act(() => {
          if (navigator.clipboard) navigator.clipboard.writeText(embed.href).catch(() => {});
        })} />}
        {(embed.w > 0 || embed.h > 0) &&
        <EmbedItem label="Reset size" light={light}
          onClick={act(() => onEdit && onEdit(window.mdEmbedResize(raw, 0, 0)))} />}
        <EmbedItem label="Remove" light={light} danger hint="Takes it out of the writing"
          onClick={act(() => onEdit && onEdit(''))} />
      </div>}

      {/* The corner. Big enough to find with a thumb, quiet enough to ignore —
          and it is the SAME corner the whole app resizes from. */}
      <span {...hover} onPointerDown={onGrab} role="presentation"
        style={zone({ insetInlineEnd: 0, bottom: 0, width: 28, height: 28,
          cursor: 'nwse-resize', display: 'grid', placeItems: 'center', touchAction: 'none' })}>
        <span style={{ width: 14, height: 14, borderRadius: 5,
          borderInlineEnd: `2.5px solid ${rgbaE('#ffffff', 0.75)}`,
          borderBottom: `2.5px solid ${rgbaE('#ffffff', 0.75)}`,
          filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.45))',
          opacity: shown ? 1 : 0, transition: 'opacity 140ms ease' }} />
      </span>
    </div>);
}

/* `on` makes the row a CHOICE rather than an action: it is announced as one to
   a screen reader (menuitemradio, checked) and drawn as one, with a tick in a
   gutter every row shares — so the labels stay in a column and the current
   format is legible at a glance rather than by reading all four. */
function EmbedItem({ label, hint, onClick, light, accent, on = null, danger = false }) {
  const [hot, setHot] = useStateE(false);
  const choice = on !== null;
  return (
    <button role={choice ? 'menuitemradio' : 'menuitem'} aria-checked={choice ? !!on : undefined}
      onClick={onClick}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)} onBlur={() => setHot(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
        padding: hint ? '7px 14px 8px' : '8px 14px', border: 'none',
        background: hot ? inkE(light ? 0.05 : 0.08) : 'transparent',
        cursor: 'pointer', fontFamily: 'inherit' }}>
      {choice &&
      <span aria-hidden="true" style={{ width: 13, flexShrink: 0, display: 'grid', placeItems: 'center' }}>
        {on &&
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M5 12.5l4.4 4.5L19 7.5" stroke={rgbaE(accent || '#7C68E6', 1)} strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>}
      </span>}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: on ? 640 : 560,
          color: danger ? (light ? '#c0263f' : '#ff6b81') : inkE(0.88) }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: 11.5, marginTop: 1.5,
          color: inkE(0.42), fontWeight: 500 }}>{hint}</span>}
      </span>
    </button>);
}

/* What the editor asks, per line, before it lays anything out: is this line a
   card, and if so how big a box should the writing reserve for it? One
   implementation, because the record sheet and a project's page are the same
   editor and a link cannot mean two different things depending which is open. */
function mdEmbedBlockOf(line) {
  const e = window.mdEmbedLine ? window.mdEmbedLine(line) : null;
  return e ? mdEmbedBox(e) : null;
}

Object.assign(window, { MediaFrame, FileFrame, BookmarkCard, EmbedView, EmbedCard,
  mdEmbedBox, mdEmbedBlockOf, mdEmbedFormatsOf, mdEmbedName, MD_EMBED_FILE_W });
